import React from 'react';
import { XCircle, AlertTriangle, Info, Activity, Zap } from 'lucide-react';

import { ScanSummary } from '../types/index';

interface MetricsDashboardProps {
    summary: ScanSummary;
    scanDuration: number;
    url: string;
}

export const MetricsDashboard: React.FC<MetricsDashboardProps> = ({ summary, scanDuration, url }) => {
    // Calculate overall score (0-100)
    const calculateScore = () => {
        const maxPenalty = 100;
        const penalties = {
            critical: 25,
            serious: 15,
            moderate: 8,
            minor: 3
        };

        const totalPenalty =
            summary.criticalIssues * penalties.critical +
            summary.seriousIssues * penalties.serious +
            summary.moderateIssues * penalties.moderate +
            summary.minorIssues * penalties.minor;

        return Math.max(0, 100 - Math.min(maxPenalty, totalPenalty));
    };

    const score = calculateScore();

    const getScoreColor = (score: number) => {
        if (score >= 90) return 'text-success-600';
        if (score >= 70) return 'text-warning-600';
        return 'text-error-600';
    };

    return (
        <div className="space-y-6">
            {/* Top Summary Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Overall Score */}
                <div className="glass rounded-2xl p-6 relative overflow-hidden flex flex-col items-center justify-center text-center">
                    <div className="relative w-32 h-32 flex items-center justify-center mb-4">
                        <svg className="absolute w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="45" stroke="#f1f5f9" strokeWidth="8" fill="none" />
                            <circle
                                cx="50" cy="50" r="45"
                                stroke={score >= 90 ? '#10b981' : score >= 70 ? '#f59e0b' : '#ef4444'}
                                strokeWidth="8" fill="none"
                                strokeDasharray="283"
                                strokeDashoffset={283 - (283 * score) / 100}
                                strokeLinecap="round"
                            />
                        </svg>
                        <div className="flex flex-col items-center">
                            <span className={`text-4xl font-black ${getScoreColor(score)}`}>{score}</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Score</span>
                        </div>
                    </div>
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-600">
                        <Activity className="w-3 h-3 mr-1.5" />
                        Overall Health
                    </div>
                </div>

                {/* Issue Summary Card */}
                <div className="glass rounded-2xl p-6 flex flex-col justify-between md:col-span-2">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-xl font-extrabold text-gray-900">Scan Summary</h3>
                            <p className="text-sm text-gray-500">Comprehensive analysis of {url}</p>
                        </div>
                        <div className="p-2 bg-primary-50 rounded-lg">
                            <Zap className="w-5 h-5 text-primary-600" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="p-3 bg-white/50 rounded-xl border border-white/50">
                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Total Issues</span>
                            <span className="text-2xl font-black text-gray-900">{summary.totalIssues}</span>
                        </div>
                        <div className="p-3 bg-white/50 rounded-xl border border-white/50">
                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Critical</span>
                            <span className="text-2xl font-black text-error-600">{summary.criticalIssues}</span>
                        </div>
                        <div className="p-3 bg-white/50 rounded-xl border border-white/50">
                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Duration</span>
                            <span className="text-2xl font-black text-gray-900">{(scanDuration / 1000).toFixed(1)}s</span>
                        </div>
                        <div className="p-3 bg-white/50 rounded-xl border border-white/50">
                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Compliance</span>
                            <span className={`text-xl font-black ${summary.wcagLevel === 'Non-compliant' ? 'text-error-600' : 'text-success-600'}`}>
                                {summary.wcagLevel}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Severity Breakdown */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SeverityCard label="Critical" count={summary.criticalIssues} color="red" icon={<XCircle />} />
                <SeverityCard label="Serious" count={summary.seriousIssues} color="orange" icon={<AlertTriangle />} />
                <SeverityCard label="Moderate" count={summary.moderateIssues} color="yellow" icon={<AlertTriangle />} />
                <SeverityCard label="Minor" count={summary.minorIssues} color="blue" icon={<Info />} />
            </div>
        </div>
    );
};

const SeverityCard = ({ label, count, color, icon }: { label: string, count: number, color: string, icon: React.ReactNode }) => {
    const colorMap: any = {
        red: 'border-error-500 text-error-500 bg-error-50/50',
        orange: 'border-warning-500 text-warning-500 bg-warning-50/50',
        yellow: 'border-amber-400 text-amber-500 bg-amber-50/50',
        blue: 'border-info-500 text-info-500 bg-info-50/50'
    };

    return (
        <div className={`glass p-5 rounded-2xl border-b-4 ${colorMap[color]} transition-all hover:-translate-y-1 hover:shadow-lg`}>
            <div className="flex justify-between items-start mb-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</span>
                <div className="p-1 rounded bg-white/50 shadow-sm">
                    {React.cloneElement(icon as React.ReactElement, { className: 'w-4 h-4' })}
                </div>
            </div>
            <p className="text-3xl font-black text-gray-900 mt-auto">{count}</p>
        </div>
    );
};
